export interface PriorityNavItem {
  key: string;
  width: number;
  active: boolean;
}

export interface PriorityNavSplit {
  visibleKeys: string[];
  overflowKeys: string[];
  moreActive: boolean;
}

/**
 * Priority+ nav: keep links in source order until they stop fitting,
 * then move the rest under a "more" button. The active link stays visible
 * by taking the place of the last link that still fits.
 */
export function splitPriorityNav(
  items: PriorityNavItem[],
  available: number,
  moreWidth: number,
  gap: number,
): PriorityNavSplit {
  const measurable = items.filter((item) => item.width > 0 && item.key);
  const showAll = (): PriorityNavSplit => ({
    visibleKeys: measurable.map((item) => item.key),
    overflowKeys: [],
    moreActive: false,
  });

  if (measurable.length === 0 || available <= 0) {
    return showAll();
  }

  const widthOf = (keys: string[], reserveMore: boolean) => {
    let used = 0;
    keys.forEach((key, index) => {
      const item = measurable.find((entry) => entry.key === key);
      used += (item?.width ?? 0) + (index > 0 ? gap : 0);
    });
    if (reserveMore) {
      used += moreWidth + (keys.length > 0 ? gap : 0);
    }
    return used;
  };

  const fits = (keys: string[]) => widthOf(keys, keys.length < measurable.length) <= available;

  const visible: string[] = [];
  for (const item of measurable) {
    const candidate = [...visible, item.key];
    if (fits(candidate)) {
      visible.push(item.key);
    } else {
      break;
    }
  }

  const active = measurable.find((item) => item.active);
  let visibleKeys = visible;

  if (active && !visible.includes(active.key)) {
    const prefix = [...visible];
    let pulled: string[] | null = null;

    while (prefix.length > 0) {
      const candidate = [...prefix.slice(0, -1), active.key];
      if (fits(candidate)) {
        pulled = candidate;
        break;
      }
      prefix.pop();
    }

    if (!pulled && fits([active.key])) {
      pulled = [active.key];
    }

    if (pulled) {
      visibleKeys = pulled;
    }
  }

  const visibleSet = new Set(visibleKeys);
  const overflowKeys = measurable.filter((item) => !visibleSet.has(item.key)).map((item) => item.key);

  return {
    visibleKeys,
    overflowKeys,
    moreActive: measurable.some((item) => item.active && !visibleSet.has(item.key)),
  };
}
